import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ApartmentIcon from '@mui/icons-material/Apartment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CampaignIcon from '@mui/icons-material/Campaign';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import EventIcon from '@mui/icons-material/Event';
import FolderIcon from '@mui/icons-material/Folder';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import HistoryIcon from '@mui/icons-material/History';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentsIcon from '@mui/icons-material/Payments';
import PeopleIcon from '@mui/icons-material/People';
import WidgetsIcon from '@mui/icons-material/Widgets';
import type { ComponentType } from 'react';

import { hasAnyRole, ROLES, type Role } from '@/auth/roles';

import {
  ACCOUNT_ENTRIES,
  CONTENT_SECTIONS,
  STAFF_SECTION_ENTRIES,
  type PathEntry,
  type SectionEntry,
} from './paths';
import { ROUTES } from './routes';

export type NavChild = PathEntry;

export interface NavSection extends Omit<SectionEntry, 'children' | 'action'> {
  icon: ComponentType;
  children?: readonly NavChild[];
  action?: NavChild;
}

/**
 * Where a role belongs when it has not asked for anywhere in particular.
 *
 * Activist staff administer organization accounts; they do not have a
 * dashboard, because they do not have an organization. Sending them to one and
 * letting the guard refuse it would make "You do not have access to that page"
 * the first thing they see after signing in.
 */
export function landingFor(role: Role | null | undefined): string {
  return role === ROLES.ACTIVIST_STAFF ? ROUTES.staff.organizations : ROUTES.dashboard;
}

/**
 * The way back for a role, named for where it actually goes.
 *
 * The refusal and not-found pages both offer it. The refusal page used to say
 * "Back to the dashboard" to Activist staff, who have no dashboard - and who
 * had often just been refused one.
 */
export function landingLink(role: Role | null | undefined): {
  to: string;
  label: string;
} {
  return role === ROLES.ACTIVIST_STAFF
    ? { to: landingFor(role), label: 'Back to organizations' }
    : { to: landingFor(role), label: 'Back to the dashboard' };
}

/**
 * The icon drawn beside each section. Keyed by the section's address, and typed
 * so that a section added to `paths.ts` without an icon fails to compile.
 *
 * These tables are named in camel case because the fast-refresh lint rule
 * takes any capitalised name in a .tsx file for a component.
 */
const sectionIcons: Record<(typeof CONTENT_SECTIONS)[number]['path'], ComponentType> = {
  [ROUTES.dashboard]: DashboardIcon,
  [ROUTES.notifications.root]: NotificationsIcon,
  [ROUTES.arc.root]: WidgetsIcon,
  [ROUTES.campaigns.root]: CampaignIcon,
  [ROUTES.petitions.root]: HistoryEduIcon,
  [ROUTES.events.root]: EventIcon,
  [ROUTES.guides.root]: HowToVoteIcon,
  [ROUTES.surveys.root]: AssignmentIcon,
  [ROUTES.pages.root]: DescriptionIcon,
  [ROUTES.documents.root]: FolderIcon,
  [ROUTES.supporters.root]: PeopleIcon,
  [ROUTES.help]: HelpOutlineIcon,
};

const staffIcons: Record<(typeof STAFF_SECTION_ENTRIES)[number]['path'], ComponentType> =
  {
    [ROUTES.staff.organizations]: ApartmentIcon,
    [ROUTES.staff.activity]: HistoryIcon,
  };

/**
 * The twelve sections of the portal, taken from the wireframes.
 *
 * The sections themselves - labels, addresses, who may use them - are in
 * `paths.ts`, where the route table and the browser tests read them too. This
 * adds only what the menu draws.
 */
export const NAV_SECTIONS: NavSection[] = CONTENT_SECTIONS.map((section) => ({
  ...section,
  icon: sectionIcons[section.path],
}));

/**
 * The Activist staff area.
 *
 * There is no wireframe for this. It follows the same shape as the
 * organization-facing sections so the two halves of the portal look like one
 * product, and it is a separate list because staff are not an organization role
 * at all - they manage accounts rather than belonging to one.
 */
export const STAFF_SECTIONS: NavSection[] = STAFF_SECTION_ENTRIES.map((section) => ({
  ...section,
  icon: staffIcons[section.path],
}));

/**
 * The account rail, from the wireframes, with the icon drawn beside each label.
 * The entries and their roles are in `paths.ts`.
 */
export interface AccountItem extends NavChild {
  /** Drawn beside the label in the rail, as the wireframe shows. */
  icon: ComponentType;
}

const accountIcons: Record<(typeof ACCOUNT_ENTRIES)[number]['path'], ComponentType> = {
  [ROUTES.account.profile]: AccountBoxIcon,
  [ROUTES.account.account]: BusinessCenterIcon,
  [ROUTES.account.users]: ManageAccountsIcon,
  [ROUTES.account.billing]: PaymentsIcon,
  [ROUTES.account.activity]: HistoryIcon,
};

export const ACCOUNT_ITEMS: AccountItem[] = ACCOUNT_ENTRIES.map((item) => ({
  ...item,
  icon: accountIcons[item.path],
}));

/**
 * The account entries one person may use, for the rail and the avatar menu
 * alike, so the two can never list different things.
 *
 * All of them while staff are viewing an organization - the route guard lets
 * them through, read-only - and otherwise only those their role allows. Log
 * Out is not here: every list adds it itself, because it ends a session rather
 * than leading to a page.
 */
export function accountEntries(role: Role | null, viewing: boolean): AccountItem[] {
  if (viewing) return ACCOUNT_ITEMS;
  return ACCOUNT_ITEMS.filter((item) => !item.allow || hasAnyRole(role, item.allow));
}
